/**
 * Treeline COG service
 *
 * Reads the pre-computed global *climatic* treeline-elevation COG (Körner model
 * over CHELSA + DEM — see scripts/treeline/) by HTTP range request, exposing the
 * treeline elevation (meters) at a point or over a lat/lng mesh. This replaces
 * the former on-the-fly DEM + land-cover estimator for both the treeline map
 * layer and the View's Below/Near/Above-treeline classification — a single,
 * smooth, slowly-varying surface instead of a per-viewport re-derivation.
 *
 * Served from our COG bucket at /data/treeline/ (CloudFront), the same way the
 * SNODAS snow-depth COGs are served at /data/snodas/.
 */

import { Capacitor } from '@capacitor/core'
import { fromUrl, type GeoTIFF, type GeoTIFFImage } from 'geotiff'
import { logger } from '../../utils/logger.js'

const log = logger.create('treeline')

const COG_ORIGIN = Capacitor.isNativePlatform() ? 'https://taselith.com' : ''
// Versioned filename (not invalidation) is how we bust CloudFront for this COG —
// edge invalidations proved unreliable here. Bump the version on each re-deploy.
const COG_URL = `${COG_ORIGIN}/data/treeline/treeline_v2.tif`
/** Matches the generator's NODATA (scripts/treeline/generate_treeline_cog.py). */
const NODATA = -9999

/** Run a COG range read, retrying once on failure. The treeline COG is served
 *  from CloudFront where a cold-cache range fetch can occasionally 5xx; a single
 *  immediate retry self-heals that transient instead of failing the whole read. */
async function readWithRetry<T>(read: () => Promise<T>): Promise<T> {
  try {
    return await read()
  } catch {
    return read()
  }
}

class TreelineCogService {
  private tiffPromise: Promise<GeoTIFF> | null = null
  private imagePromise: Promise<GeoTIFFImage> | null = null

  /** Lazily open the COG (one HTTP range read of the header), cached. A failure
   *  clears the cache so a later call retries rather than failing forever. */
  private async getImage(): Promise<GeoTIFFImage> {
    if (!this.imagePromise) {
      this.tiffPromise ??= fromUrl(COG_URL)
      this.imagePromise = this.tiffPromise
        .then(t => t.getImage(0))
        .catch(err => {
          this.tiffPromise = null
          this.imagePromise = null
          throw err
        })
    }
    return this.imagePromise
  }

  /**
   * Treeline elevation (m) sampled over a lat/lng mesh, row-major (k = i*W + j).
   * Mirrors `elevationPointService.getElevationGrid`: `lats` descending
   * (north→south), `lngs` ascending (west→east). One windowed range read covers
   * the whole mesh; NODATA / out-of-coverage pixels come back as `null`.
   */
  async getTreelineGrid(lats: number[], lngs: number[]): Promise<(number | null)[]> {
    const H = lats.length
    const W = lngs.length
    const out = new Array<number | null>(H * W).fill(null)
    if (H < 1 || W < 1) return out

    try {
      const image = await this.getImage()
      const bbox = image.getBoundingBox() // [west, south, east, north]
      const imgW = image.getWidth()
      const imgH = image.getHeight()
      const pxPerDegX = imgW / (bbox[2] - bbox[0])
      const pxPerDegY = imgH / (bbox[3] - bbox[1])

      const west = Math.min(lngs[0], lngs[W - 1])
      const east = Math.max(lngs[0], lngs[W - 1])
      const south = Math.min(lats[0], lats[H - 1])
      const north = Math.max(lats[0], lats[H - 1])

      const clW = Math.max(west, bbox[0])
      const clE = Math.min(east, bbox[2])
      const clS = Math.max(south, bbox[1])
      const clN = Math.min(north, bbox[3])
      if (clW >= clE || clS >= clN) return out

      const winLeft = Math.max(0, Math.floor((clW - bbox[0]) * pxPerDegX))
      const winRight = Math.min(imgW, Math.ceil((clE - bbox[0]) * pxPerDegX))
      const winTop = Math.max(0, Math.floor((bbox[3] - clN) * pxPerDegY))
      const winBottom = Math.min(imgH, Math.ceil((bbox[3] - clS) * pxPerDegY))
      if (winRight <= winLeft || winBottom <= winTop) return out

      // Resample the window to the requested mesh. Our lats/lngs are evenly
      // spaced across the same extent, so the W×H samples align (sub-pixel
      // offsets are negligible against ~1 km treeline data). One retry absorbs a
      // transient edge hiccup (e.g. a cold-cache 5xx on a range fetch) so a single
      // blip doesn't blank the treeline widget for the whole View.
      const rasters = await readWithRetry(() =>
        image.readRasters({
          window: [winLeft, winTop, winRight, winBottom],
          width: W,
          height: H,
          interleave: false,
        })
      )
      const px = rasters[0] as unknown as ArrayLike<number>
      for (let i = 0; i < H * W; i++) {
        const v = px[i]
        out[i] = v === NODATA || !Number.isFinite(v) ? null : v
      }
      return out
    } catch (err) {
      log.warn('treeline grid query failed', err)
      return out
    }
  }

  /** Treeline elevation (m) at a single point, or `null` (NODATA / out of coverage). */
  async treelineAt(lat: number, lng: number): Promise<number | null> {
    try {
      const image = await this.getImage()
      const bbox = image.getBoundingBox()
      const imgW = image.getWidth()
      const imgH = image.getHeight()
      const px = Math.floor(((lng - bbox[0]) / (bbox[2] - bbox[0])) * imgW)
      const py = Math.floor(((bbox[3] - lat) / (bbox[3] - bbox[1])) * imgH)
      if (px < 0 || px >= imgW || py < 0 || py >= imgH) return null

      const rasters = await readWithRetry(() =>
        image.readRasters({
          window: [px, py, px + 1, py + 1],
          width: 1,
          height: 1,
          interleave: false,
        })
      )
      const v = (rasters[0] as unknown as number[])[0]
      return v === NODATA || !Number.isFinite(v) ? null : v
    } catch (err) {
      log.warn('treeline point query failed', err)
      return null
    }
  }
}

export const treelineCogService = new TreelineCogService()
